"use client"

import { useState, useEffect } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useInfiniteQuery } from "@tanstack/react-query"
import { Search, Filter, XCircle } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { getCourses } from "@/lib/services/courseService"
import GridItem from "@/components/course/gridItem"
import { useInView } from "react-intersection-observer"
import type { CourseDetail } from "@/lib/types/course/detail"

interface SearchClientProps {
	initialQuery?: string
}

export function SearchClient({ initialQuery = "" }: SearchClientProps) {
	const router = useRouter()
	const searchParams = useSearchParams()
	const [query, setQuery] = useState(
		searchParams.get("q") || searchParams.get("value") || initialQuery
	)
	const [sortBy, setSortBy] = useState(
		searchParams.get("sort") || "newest",
	)
	const { ref: loadMoreRef, inView } = useInView()

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery({
			queryKey: ["courses", query, sortBy],
			queryFn: ({ pageParam = 1 }) =>
				getCourses({
					search: query,
					sort: sortBy as any,
					page: pageParam,
					per_page: 8,
				}),
			getNextPageParam: (lastPage) =>
				lastPage.paginate.current_page < lastPage.paginate.last_page
					? lastPage.paginate.current_page + 1
					: undefined,
			initialPageParam: 1,
		})

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage()
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault()
		const params = new URLSearchParams()
		if (query.trim()) params.set("q", query.trim())
		if (sortBy !== "newest") params.set("sort", sortBy)
		router.push(`/course/search?${params.toString()}`)
	}

	const handleSortChange = (value: string) => {
		setSortBy(value)
		const params = new URLSearchParams(searchParams.toString())
		if (value !== "newest") {
			params.set("sort", value)
		} else {
			params.delete("sort")
		}
		router.push(`/course/search?${params.toString()}`)
	}

	const clearSearch = () => {
		setQuery("")
		setSortBy("newest")
		router.push("/course/search")
	}

	const allCourses = data?.pages.flatMap((page) => page.data) ?? []

	return (
		<div className="space-y-6">
			<form onSubmit={handleSearch} className="flex flex-col gap-4 sm:flex-row">
				<div className="relative flex-1">
					<Search className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 text-gray-400" />
					<Input
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						className="w-full pl-10"
						placeholder="Tìm kiếm khóa học..."
					/>
					{query && (
						<button
							type="button"
							onClick={clearSearch}
							className="-translate-y-1/2 absolute top-1/2 right-3 text-gray-400 hover:text-gray-600"
						>
							<XCircle className="h-5 w-5" />
						</button>
					)}
				</div>
				<div className="flex justify-between gap-4">
					<Select value={sortBy} onValueChange={handleSortChange}>
						<SelectTrigger className="w-[180px]">
							<Filter className="mr-2 h-4 w-4" />
							<SelectValue placeholder="Sắp xếp theo" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="most_learned">Học nhiều nhất</SelectItem>
							<SelectItem value="highest_rated">Đánh giá cao nhất</SelectItem>
							<SelectItem value="newest">Mới nhất</SelectItem>
							<SelectItem value="price_low_to_high">Giá tăng dần</SelectItem>
							<SelectItem value="price_high_to_low">Giá giảm dần</SelectItem>
							<SelectItem value="free">Miễn phí</SelectItem>
							<SelectItem value="upcoming">Sắp ra mắt</SelectItem>
						</SelectContent>
					</Select>
					<Button type="submit">Tìm kiếm</Button>
				</div>
			</form>

			{isLoading ? (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{Array.from({ length: 8 }, (_, i) => (
						<div key={`initial-skeleton-${Date.now()}-${i}`} className="animate-pulse">
							<div className="h-48 w-full rounded-lg bg-gray-200" />
						</div>
					))}
				</div>
			) : !allCourses.length ? (
				<div className="flex flex-col items-center justify-center py-12">
					<p className="text-center text-gray-500">
						{query
							? `Không tìm thấy khóa học phù hợp với "${query}"`
							: "Vui lòng nhập từ khóa để tìm kiếm khóa học"}
					</p>
				</div>
			) : (
				<>
					<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
						{allCourses.map((course: CourseDetail) => (
							<GridItem key={course.id} data={course} type="course" />
						))}
					</div>

					{/* Loading more indicator */}
					{isFetchingNextPage && (
						<div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
							{Array.from({ length: 4 }, (_, i) => (
								<div key={`infinite-skeleton-${Date.now()}-${i}`} className="animate-pulse">
									<div className="h-48 w-full rounded-lg bg-gray-200" />
								</div>
							))}
						</div>
					)}

					{/* Intersection observer target */}
					<div ref={loadMoreRef} className="h-4 w-full" />
				</>
			)}
		</div>
	)
}
